<template>
  <box-default>
    <div class="col-xs-12">
      <div class="default-box pi-page">
        <h1 class="pi-page__title">Product Intelligence</h1>
        <pi-tabs current="activation" v-if="backRoute.view === 'activation'"/>
        <pi-tabs current="features" v-else-if="backRoute.view === 'features'"/>
        <pi-tabs current="assistant" v-else-if="backRoute.view === 'assistant'"/>
        <pi-tabs current="companies" v-else/>
        <div class="pi-company__top">
          <router-link class="pi-link pi-back" :to="backLink">← {{ backRoute.label }}</router-link>
          <pi-export-screen :screen="exportScreenName" :filters="exportFilters" :period="exportPeriodValue"/>
        </div>
        <pi-filters v-model="filters" :fields="filterFields" @clear="clearFilters"/>

        <pi-data-state :loading="detail.loading" :error="detail.error" :envelope="detail.envelope" :empty="isEmpty"
                       empty-message="Sem dados desta empresa no período." :show-footer="false" @retry="loadDetail(true)">
          <section class="pi-section" aria-labelledby="pi-company-title">
            <div class="pi-section__head pi-company__head">
              <h2 id="pi-company-title" class="pi-section__title">{{ view.company.name }}</h2>
              <span class="pi-company__badges">
                <span class="pi-badge" :class="'pi-badge--' + view.company.status.tone">{{ view.company.status.label }}</span>
                <pi-definition-tooltip info-key="common.companyStatus" label="Situação"/>
                <span v-if="isDemo" class="pi-badge pi-badge--muted" :title="demoBadgeTitle">Demonstração</span>
              </span>
              <pi-company-demo v-if="valid" :company-id="ids.companyId" :shard-code="ids.shardCode" :company-name="view.company.name"
                               :pi="piDemo" @changed="onDemoChanged"/>
            </div>
            <dl class="pi-company__facts">
              <div v-for="fact in facts" :key="fact.key" class="pi-company__fact">
                <dt>{{ fact.label }}<pi-definition-tooltip class="pi-company__info" :info-key="fact.infoKey" :label="fact.label"/></dt>
                <dd>{{ fact.value }}</dd>
              </div>
            </dl>
            <p class="pi-company__links">
              <router-link v-if="accountLink" :to="accountLink" class="pi-link">Abrir a conta no Master</router-link>
              <router-link v-if="similarLink" :to="similarLink" class="pi-link">Empresas semelhantes (mesmo porte)</router-link>
            </p>
          </section>

          <section class="pi-section" aria-labelledby="pi-company-peers">
            <div class="pi-section__head">
              <h2 id="pi-company-peers" class="pi-section__title">Comparação com empresas semelhantes</h2>
              <pi-state-legend/>
              <pi-export-menu title="Pares" :screen="exportScreenName" :columns="peerColumns" :rows="peerRows" :envelope="detail.envelope"
                              :filters="exportFilters" :period="exportPeriodValue" :disabled="isDemo || !peerRows.length"/>
            </div>
            <p v-if="isDemo" class="pi-muted pi-company__hidden"><span class="pi-unmeasured" aria-hidden="true">—</span> {{ activityHiddenText }}</p>
            <div v-else class="row">
              <div class="col-xs-12 col-sm-6 col-md-4">
                <div class="pi-stat">
                  <span class="pi-stat__label">Active Seats Rate da empresa<pi-definition-tooltip class="pi-company__info" info-key="common.activeSeatsRate"
                                                                                               label="Active Seats Rate da empresa" :version="definitionVersion"/></span>
                  <span class="pi-stat__value"><pi-metric-cell :cell="peers.company"/></span>
                  <span v-if="rowRequest.loading" class="pi-stat__sub">Carregando…</span>
                  <span v-else-if="listRowError" class="pi-stat__sub">{{ listRowError }}</span>
                </div>
              </div>
              <div class="col-xs-12 col-sm-6 col-md-4">
                <div class="pi-stat">
                  <span class="pi-stat__label">Mediana dos pares{{ peers.sizeBandText !== '—' ? ' (' + peers.sizeBandText + ')' : '' }}<pi-definition-tooltip
                    class="pi-company__info" info-key="companies.peerMedian" label="Mediana dos pares"/></span>
                  <span class="pi-stat__value"><pi-metric-cell :cell="peers.peer"/></span>
                </div>
              </div>
              <div class="col-xs-12 col-md-4">
                <p v-if="peers.gapText" class="pi-company__gap">
                  <span class="pi-badge" :class="'pi-badge--' + peers.gapTone">{{ peers.gapText }}</span>
                  <pi-definition-tooltip info-key="companies.peerGap" label="Distância da mediana dos pares"/>
                </p>
                <p v-else class="pi-muted">Sem base para comparar: um dos dois valores não está disponível.</p>
              </div>
            </div>
          </section>

          <section class="pi-section" aria-labelledby="pi-company-evolution">
            <div class="pi-section__head">
              <h2 id="pi-company-evolution" class="pi-section__title">
                Evolução<pi-definition-tooltip class="pi-company__info" info-key="companies.evolution" label="Evolução"/>
              </h2>
              <p class="pi-section__hint">{{ periodText }}</p>
              <pi-export-menu title="Série" :screen="exportScreenName" :columns="seriesColumns" :rows="view.series" :envelope="detail.envelope"
                              :filters="exportFilters" :period="exportPeriodValue" :disabled="isDemo || !view.series.length"/>
            </div>
            <p v-if="isDemo" class="pi-muted pi-company__hidden"><span class="pi-unmeasured" aria-hidden="true">—</span> {{ activityHiddenText }}</p>
            <div v-else class="pi-chart-grid">
              <div class="pi-chart-block">
                <pi-line-chart title="Usuários ativos e em uso" :series="view.activitySeries"/>
              </div>
              <div class="pi-chart-block">
                <pi-line-chart title="Batidas registradas" :series="view.punchSeries"/>
              </div>
            </div>
          </section>

          <section class="pi-section" aria-labelledby="pi-company-features">
            <div class="pi-section__head">
              <h2 id="pi-company-features" class="pi-section__title">Funcionalidades</h2>
              <pi-state-legend/>
              <pi-export-menu title="Funcionalidades" :screen="exportScreenName" :columns="featureColumns" :rows="view.featureRows"
                              :envelope="detail.envelope" :filters="exportFilters" :period="exportPeriodValue" :disabled="isDemo || !view.features.length"/>
            </div>
            <p v-if="isDemo" class="pi-muted pi-company__hidden"><span class="pi-unmeasured" aria-hidden="true">—</span> {{ activityHiddenText }}</p>
            <div v-else-if="view.features.length" class="pi-table-wrap">
              <table class="pi-table">
                <caption class="pi-sr">Adoção das funcionalidades na empresa</caption>
                <thead>
                <tr>
                  <pi-th v-for="column in featureHeaders" :key="column.key" :column="column"/>
                </tr>
                </thead>
                <tbody>
                <tr v-for="feature in view.features" :key="feature.key">
                  <td>
                    <router-link v-if="featureLink(feature)" :to="featureLink(feature)" class="pi-link">{{ feature.name }}</router-link>
                    <span v-else>{{ feature.name }}</span>
                    <span class="pi-table__sub">{{ feature.moduleText }}</span>
                  </td>
                  <td class="is-num">{{ feature.usersText || '—' }}</td>
                  <td class="is-num"><pi-metric-cell :cell="feature.adoption" :show-ratio="false"/></td>
                </tr>
                </tbody>
              </table>
            </div>
            <p v-else-if="breakdownUnavailable" class="pi-muted">n/d: {{ breakdownUnavailable }}</p>
            <p v-else class="pi-muted">Nenhuma funcionalidade elegível com dados no período.</p>
          </section>

          <section class="pi-section" aria-labelledby="pi-company-operations">
            <div class="pi-section__head">
              <h2 id="pi-company-operations" class="pi-section__title">Operações</h2>
              <pi-state-legend/>
              <pi-export-menu title="Operações" :screen="exportScreenName" :columns="operationColumns" :rows="view.operations"
                              :envelope="detail.envelope" :filters="exportFilters" :period="exportPeriodValue" :disabled="isDemo || !view.operations.length"/>
            </div>
            <p v-if="isDemo" class="pi-muted pi-company__hidden"><span class="pi-unmeasured" aria-hidden="true">—</span> {{ activityHiddenText }}</p>
            <div v-else-if="view.operations.length" class="pi-table-wrap">
              <table class="pi-table">
                <caption class="pi-sr">Confiabilidade das operações na empresa</caption>
                <thead>
                <tr>
                  <pi-th v-for="column in operationHeaders" :key="column.key" :column="column"/>
                </tr>
                </thead>
                <tbody>
                <tr v-for="operation in view.operations" :key="operation.key">
                  <td>{{ operation.label }}<span v-if="operation.note" class="pi-table__sub">{{ operation.note }}</span></td>
                  <td class="is-num" :title="operation.failureTitle || null">{{ operation.attemptsText }}</td>
                  <td class="is-num">{{ operation.successText }}</td>
                  <td class="is-num" :title="operation.failureTitle || null">{{ operation.businessText }}</td>
                  <td class="is-num" :title="operation.failureTitle || null">{{ operation.technicalText }}</td>
                  <td class="is-num"><pi-metric-cell :cell="operation.successRate"/></td>
                </tr>
                </tbody>
              </table>
            </div>
            <p v-else-if="breakdownUnavailable" class="pi-muted">n/d: {{ breakdownUnavailable }}</p>
            <p v-else class="pi-muted">Nenhuma operação medida no período.</p>
          </section>

          <section class="pi-section" aria-labelledby="pi-company-insights">
            <div class="pi-section__head">
              <h2 id="pi-company-insights" class="pi-section__title">Insights da conta</h2>
            </div>
            <pi-insight-list :insights="view.insights" empty-text="Nenhum insight aberto para esta empresa."/>
          </section>
        </pi-data-state>

        <section v-if="valid" class="pi-section" aria-labelledby="pi-company-onboarding">
          <div class="pi-section__head">
            <h2 id="pi-company-onboarding" class="pi-section__title">Ativação</h2>
            <p class="pi-section__hint">D0 = data da venda · marcos até D+15, D+30 e D+60 · não depende do período escolhido</p>
          </div>
          <pi-data-state :loading="onboarding.loading" :error="onboarding.error" :envelope="onboardingEnvelope" :empty="onboardingEmpty"
                         empty-message="Empresa fora do acompanhamento de ativação (interna, sem venda no horizonte de 400 dias ou ainda não lida)."
                         :show-footer="false" @retry="loadOnboarding(true)">
            <pi-onboarding-company :view="onboardingView" :definition-version="onboardingVersion">
              <p class="pi-company__links">
                <router-link :to="activationLink" class="pi-link">Ver na Ativação</router-link>
              </p>
            </pi-onboarding-company>
          </pi-data-state>
        </section>

        <section v-if="revenueVisible && valid" class="pi-section" aria-labelledby="pi-company-portfolio">
          <div class="pi-section__head">
            <h2 id="pi-company-portfolio" class="pi-section__title">Portfólio</h2>
            <p class="pi-section__hint">Estado comercial por evidência de liberação e venda, sem item de contrato por funcionalidade e sem valor estimado.</p>
            <pi-export-menu title="Portfólio" :screen="exportScreenName" :columns="portfolioColumns" :rows="portfolioRows" :envelope="portfolio.envelope"
                            :filters="exportFilters" :period="exportPeriodValue" :disabled="!portfolioRows.length || !!portfolio.error"/>
          </div>
          <pi-data-state :loading="portfolio.loading" :error="portfolioFailure" :envelope="portfolio.envelope" :empty="portfolioEmpty"
                         empty-message="Nenhuma funcionalidade no portfólio desta empresa." :show-footer="false" @retry="loadPortfolio(true)">
            <p v-if="portfolioSummary" class="pi-muted pi-company__portfolio-summary">{{ portfolioSummary }}</p>
            <div v-for="group in portfolioData.modules" :key="group.key" class="pi-table-wrap pi-company__portfolio-module">
              <table class="pi-table">
                <caption class="pi-company__portfolio-caption">{{ group.label }}</caption>
                <thead>
                <tr>
                  <pi-th v-for="column in portfolioHeaders" :key="column.key" :column="column"/>
                </tr>
                </thead>
                <tbody>
                <tr v-for="row in group.rows" :key="row.key">
                  <th scope="row" class="pi-company__portfolio-name">
                    <router-link v-if="featureLink(row)" :to="featureLink(row)" class="pi-link">{{ row.name }}</router-link>
                    <span v-else>{{ row.name }}</span>
                    <span v-if="row.classText" class="pi-table__sub">{{ row.classText }}</span>
                  </th>
                  <td>{{ row.stateText }}</td>
                  <td>{{ row.sellableText }}</td>
                  <td>{{ row.measuredText }}</td>
                  <td>{{ row.usageText }}<span v-if="row.usageDetailText" class="pi-table__sub">{{ row.usageDetailText }}</span></td>
                  <td>
                    <router-link v-if="row.opportunityLink" :to="row.opportunityLink" class="pi-link"
                                 :aria-label="'Abrir a oportunidade de ' + row.name">Abrir a oportunidade</router-link>
                    <span v-else>—</span>
                  </td>
                </tr>
                </tbody>
              </table>
            </div>
          </pi-data-state>
        </section>

        <pi-page-footer :envelopes="[detail.envelope, rowRequest.envelope, onboarding.envelope, portfolio.envelope]"
                        :labels="['Empresa', 'Lista de empresas', 'Ativação', 'Portfólio']"/>
      </div>
    </div>
  </box-default>
</template>

<script>
import boxDefault from "../../components/layouts/boxDefault.vue";
import piTabs from "../../components/pi/piTabs.vue";
import piFilters from "../../components/pi/piFilters.vue";
import piDataState from "../../components/pi/piDataState.vue";
import piLineChart from "../../components/pi/piLineChart.vue";
import piMetricCell from "../../components/pi/piMetricCell.vue";
import piInsightList from "../../components/pi/piInsightList.vue";
import piDefinitionTooltip from "../../components/pi/piDefinitionTooltip.vue";
import piStateLegend from "../../components/pi/piStateLegend.vue";
import piPageFooter from "../../components/pi/piPageFooter.vue";
import piOnboardingCompany from "../../components/pi/piOnboardingCompany.vue";
import piTh from "../../components/pi/piTh.vue";
import piExportMenu from "../../components/pi/piExportMenu.vue";
import piExportScreen from "../../components/pi/piExportScreen.vue";
import piCompanyDemo from "../../components/pi/piCompanyDemo.vue";
import { listFilterState } from "../../components/default/listFilterState";
import { loadFeatureOptions, peekRevenueVisibility, piGet, resolveRevenueVisibility } from "../../components/pi/piApi.js";
import { screenColumns } from "../../components/pi/piColumns.js";
import { exportPeriod, exportProvide, filterLines } from "../../components/pi/piExport.js";
import { formatPeriod } from "../../components/pi/piFormat.js";
import {
  accountRoute, activationCompanyRoute, companyBackRoute, featureRoute, originFeatureType, originOf, viewPath,
} from "../../components/pi/piNavigation.js";
import { PI_FILTER_DEFAULTS, PI_FILTER_KEY, buildApiQuery, compareEnabled, resolvePeriod, toQueryString } from "../../components/pi/piQuery.js";
import { blankRequest, failRequest, isBlocking, payloadOf, runRequest, shouldLoad } from "../../components/pi/piRequests.js";
import {
  COMPANY_FEATURE_COLUMNS, COMPANY_OPERATION_COLUMNS, COMPANY_PEER_COLUMNS, COMPANY_SERIES_COLUMNS, companyBreakdownUnavailable, companyDetailView,
  companyExportLine, companyFacts, findCompanyRow, parseCompanyParams, peerComparison, peerExportRows, similarCompaniesQuery,
} from "../../components/pi/piCompaniesModel.js";
import { ACTIVITY_HIDDEN_TEXT, DEMO_BADGE_TITLE } from "../../components/pi/piCompanyDemoModel.js";
import { COMPANY_QUERY_KEYS, internalParam } from "../../components/pi/piCompanyFilters.js";
import { revenueError } from "../../components/pi/piOpportunitiesModel.js";
import { PORTFOLIO_COLUMNS, portfolioExportRows, portfolioView } from "../../components/pi/piPortfolioModel.js";
import { RULES_NOTE, companyOnboardingView, envelopeWithoutNotes } from "../../components/pi/piOnboardingModel.js";
import { PI_COMPANIES, PI_COMPANY_DETAIL, PI_COMPANY_ONBOARDING, PI_REVENUE_PORTFOLIO } from "../../../../urlConfig";

const FILTER_FIELDS = ["period", "compare"];

const INCLUDE_DEMO = "include";

const INVALID_PARAMS = {
  kind: "invalid",
  status: null,
  message: "O endereço não traz uma empresa e um banco válidos.",
  retryable: false,
};

export default {
  name: "pi-company-detail",
  components: {
    boxDefault, piTabs, piFilters, piDataState, piLineChart, piMetricCell, piInsightList, piDefinitionTooltip, piStateLegend, piPageFooter,
    piOnboardingCompany, piTh, piExportMenu, piExportScreen, piCompanyDemo,
  },
  mixins: [listFilterState(PI_FILTER_KEY, PI_FILTER_DEFAULTS)],
  keepQueryKeys: COMPANY_QUERY_KEYS,
  provide: exportProvide,
  data() {
    return {
      filterFields: FILTER_FIELDS,
      detail: blankRequest(),
      rowRequest: blankRequest(),
      portfolio: blankRequest(),
      onboarding: blankRequest(),
      origin: originOf(this.$route.query),
      featureName: "",
      revenueVisible: peekRevenueVisibility() === true,
      activityHiddenText: ACTIVITY_HIDDEN_TEXT,
      demoBadgeTitle: DEMO_BADGE_TITLE,
      peerColumns: COMPANY_PEER_COLUMNS,
      seriesColumns: COMPANY_SERIES_COLUMNS,
      featureColumns: COMPANY_FEATURE_COLUMNS,
      featureHeaders: screenColumns(COMPANY_FEATURE_COLUMNS),
      operationColumns: COMPANY_OPERATION_COLUMNS,
      operationHeaders: screenColumns(COMPANY_OPERATION_COLUMNS),
      portfolioColumns: PORTFOLIO_COLUMNS,
      portfolioHeaders: screenColumns(PORTFOLIO_COLUMNS),
    };
  },
  computed: {
    ids() {
      return parseCompanyParams(this.$route.params.companyId, this.$route.params.context) || { companyId: "", shardCode: "" };
    },
    valid() {
      return !!this.ids.companyId;
    },
    compare() {
      return compareEnabled(this.filters);
    },
    periodText() {
      const period = resolvePeriod(this.filters, new Date());
      return formatPeriod(period.from, period.to);
    },
    baseParams() {
      return buildApiQuery(this.filters, { fields: FILTER_FIELDS });
    },
    detailKey() {
      return this.ids.companyId + "/" + this.ids.shardCode + "?" + toQueryString(this.baseParams);
    },
    rowParams() {
      const query = buildApiQuery(this.filters, { fields: FILTER_FIELDS, extra: { search: this.ids.companyId, page: 1, pageSize: 10 } });
      return Object.assign(query, internalParam(INCLUDE_DEMO));
    },
    rowKey() {
      return this.valid ? toQueryString(this.rowParams) : "";
    },
    definitionVersion() {
      return this.detail.envelope ? this.detail.envelope.definitionVersion : null;
    },
    view() {
      return companyDetailView(payloadOf(this.detail.envelope), this.compare);
    },
    isDemo() {
      return this.view.company.isDemo;
    },
    piDemo() {
      if (!this.detail.loaded) return null;
      return { isDemo: this.view.company.isDemo, demo: this.view.company.demo };
    },
    facts() {
      return companyFacts(this.view.company, this.ids.shardCode);
    },
    breakdownUnavailable() {
      return companyBreakdownUnavailable(this.detail.envelope);
    },
    isEmpty() {
      return this.detail.loaded && !Object.keys(payloadOf(this.detail.envelope)).length;
    },
    listRow() {
      const data = payloadOf(this.rowRequest.envelope);
      return findCompanyRow(data.rows, this.ids.companyId, this.ids.shardCode);
    },
    listRowError() {
      if (this.rowRequest.error) return this.rowRequest.error.message;
      if (this.rowRequest.loaded && !this.listRow) return "A empresa não apareceu na lista do período.";
      return "";
    },
    peers() {
      return peerComparison(this.listRow ? this.listRow.activeSeatsRate : null, this.view.peers);
    },
    peerRows() {
      return this.detail.loaded ? peerExportRows(this.peers) : [];
    },
    accountLink() {
      return accountRoute(this.ids.companyId, this.ids.shardCode);
    },
    similarLink() {
      const sizeBand = this.view.company.sizeBand || (this.view.peers && this.view.peers.sizeBand);
      if (!sizeBand) return null;
      return { path: viewPath("companies"), query: similarCompaniesQuery(this.$route.query, sizeBand) };
    },
    backRoute() {
      return companyBackRoute(this.$route.query, this.origin, this.featureName);
    },
    backLink() {
      return { path: this.backRoute.path, query: this.backRoute.query };
    },
    exportScreenName() {
      return this.detail.loaded ? "Ficha da empresa: " + this.view.company.name : "Ficha da empresa";
    },
    exportFilters() {
      return filterLines({
        filters: this.filters,
        fields: FILTER_FIELDS,
        extra: [companyExportLine(this.view.company, this.ids)],
      });
    },
    exportPeriodValue() {
      return exportPeriod(this.filters);
    },
    onboardingKey() {
      return this.valid ? PI_COMPANY_ONBOARDING(this.ids.companyId, this.ids.shardCode) : "";
    },
    onboardingView() {
      return companyOnboardingView(payloadOf(this.onboarding.envelope));
    },
    onboardingVersion() {
      return this.onboarding.envelope ? this.onboarding.envelope.definitionVersion : null;
    },
    onboardingEnvelope() {
      return envelopeWithoutNotes(this.onboarding.envelope, [RULES_NOTE]);
    },
    onboardingEmpty() {
      return this.onboarding.loaded && this.onboardingView.empty;
    },
    activationLink() {
      return activationCompanyRoute(this.$route.query, this.ids.companyId, this.view.company.name !== "Empresa" ? this.view.company.name : "");
    },
    portfolioKey() {
      return this.revenueVisible && this.valid ? this.ids.companyId + "/" + this.ids.shardCode : "";
    },
    portfolioData() {
      return portfolioView(payloadOf(this.portfolio.envelope), { companyId: this.ids.companyId, name: this.view.company.name });
    },
    portfolioRows() {
      return this.portfolio.loaded ? portfolioExportRows(this.portfolioData) : [];
    },
    portfolioEmpty() {
      return this.portfolio.loaded && !this.portfolioData.total;
    },
    portfolioFailure() {
      return revenueError(this.portfolio.error);
    },
    portfolioSummary() {
      return this.portfolioData.summary.map((item) => item.text).join(" · ");
    },
  },
  watch: {
    detailKey() {
      this.loadDetail(false);
    },
    rowKey() {
      this.loadRow(false);
    },
    portfolioKey() {
      this.loadPortfolio(false);
    },
    onboardingKey() {
      this.loadOnboarding(false);
    },
  },
  created() {
    this.loadDetail(false);
    this.loadRow(false);
    this.loadPortfolio(false);
    this.loadOnboarding(false);
    this.loadFeatureName();
    resolveRevenueVisibility().then((visible) => {
      this.revenueVisible = visible;
    });
  },
  methods: {
    loadDetail(force) {
      const key = this.detailKey;
      if (!shouldLoad(this.detail, key, force)) return;
      if (!this.valid) {
        failRequest(this.detail, INVALID_PARAMS, key);
        return;
      }
      const url = PI_COMPANY_DETAIL(this.ids.companyId, this.ids.shardCode);
      runRequest(this.detail, () => piGet(url, this.baseParams), "carregar a empresa", key);
    },
    loadRow(force) {
      const key = this.rowKey;
      if (!key || !shouldLoad(this.rowRequest, key, force)) return;
      if (isBlocking(this.detail.error)) return;
      runRequest(this.rowRequest, () => piGet(PI_COMPANIES, this.rowParams), "carregar o Active Seats Rate da empresa", key);
    },
    loadPortfolio(force) {
      const key = this.portfolioKey;
      if (!key || !shouldLoad(this.portfolio, key, force)) return;
      const url = PI_REVENUE_PORTFOLIO(this.ids.companyId, this.ids.shardCode);
      runRequest(this.portfolio, () => piGet(url), "carregar o portfólio", key);
    },
    loadOnboarding(force) {
      const key = this.onboardingKey;
      if (!key || !shouldLoad(this.onboarding, key, force)) return;
      runRequest(this.onboarding, () => piGet(key), "carregar a ativação da empresa", key);
    },
    loadFeatureName() {
      const featureType = originFeatureType(this.origin);
      if (featureType === null) return;
      loadFeatureOptions()
        .then((options) => {
          const found = options.filter((option) => option.value === String(featureType))[0];
          this.featureName = found ? found.label : "";
        })
        .catch(() => {
          this.featureName = "";
        });
    },
    refreshDetail() {
      const key = this.detailKey;
      if (!this.valid || this.detail.key !== key || !this.detail.loaded || this.detail.loading) {
        this.loadDetail(true);
        return;
      }
      this.detail.seq += 1;
      const seq = this.detail.seq;
      piGet(PI_COMPANY_DETAIL(this.ids.companyId, this.ids.shardCode), this.baseParams).then((envelope) => {
        if (seq === this.detail.seq && key === this.detailKey) this.detail.envelope = envelope || null;
      }, () => {
        if (seq === this.detail.seq) this.loadDetail(true);
      });
    },
    onDemoChanged() {
      this.refreshDetail();
      this.loadRow(true);
      this.loadPortfolio(true);
    },
    featureLink(feature) {
      const route = featureRoute(feature.featureType);
      return route ? { path: route, query: this.$route.query } : null;
    },
  },
};
</script>

<style src="../../components/pi/pi.css"></style>

<style scoped>
.pi-company__top {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: 6px 16px;
}

.pi-company__head {
  justify-content: flex-start;
  align-items: center;
}

.pi-company__badges {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
}

.pi-company__info {
  margin-left: 4px;
  vertical-align: middle;
}

.pi-company__facts {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 24px;
  margin: 0 0 12px;
}

.pi-company__fact dt {
  font-size: 11px;
  font-weight: 700;
  color: #4e5560;
  text-transform: uppercase;
}

.pi-company__fact dd {
  margin: 0;
  font-size: 13px;
  color: #1f2933;
}

.pi-company__links {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 18px;
  margin: 0;
}

.pi-company__gap {
  margin: 12px 0 0;
}

.pi-company__hidden {
  font-size: 13px;
}

.pi-company__portfolio-summary {
  margin-bottom: 10px;
}

.pi-company__portfolio-module {
  margin-bottom: 14px;
}

.pi-company__portfolio-caption {
  caption-side: top;
  padding: 0 0 4px;
  font-size: 13px;
  font-weight: 700;
  color: #3a424e;
  text-align: left;
}

.pi-company__portfolio-name {
  font-weight: 400;
}
</style>



// WEBPACK FOOTER //
// src/vue/pages/restricted/productIntelligence/piCompanyDetail.vue